import React, { useEffect, useMemo, useState } from 'react';

import { ReactComponent as DownloadIcon } from 'design-system/assets/download.svg';
import { ReactComponent as EyeIcon } from 'design-system/assets/eye.svg';
import { ReactComponent as MailIcon } from 'design-system/assets/mail.svg';
import { Checkbox, Menu, Table } from 'design-system';

import {
  formatEmissionDate,
  formatInvoiceAmount,
  type InvoiceSortKey,
} from '../../invoiceDisplay';
import { isInvoiceDownloadable } from '../../helpers';
import type { BankSlipItem } from '../../types';
import {
  ActionsCell,
  ActionsMenuWrap,
  CheckboxCell,
  EmptyMessage,
  InvoiceNumber,
  InvoiceNumberCell,
  MenuTrigger,
  ReferenceLabel,
  TableDividerLine,
  TableSection,
  ViewButton,
} from './styles';

const HeaderDividerRow = (): JSX.Element => (
  <Table.Row className="divider-row" aria-hidden="true">
    <Table.HeaderCell colSpan={5}>
      <TableDividerLine />
    </Table.HeaderCell>
  </Table.Row>
);

const BodyDividerRow = ({ colSpan }: { colSpan: number }): JSX.Element => (
  <Table.Row className="divider-row" aria-hidden="true">
    <Table.Cell colSpan={colSpan}>
      <TableDividerLine />
    </Table.Cell>
  </Table.Row>
);

export type InvoiceTableProps = {
  rows: BankSlipItem[];
  selectedIds: string[];
  selectAllChecked: boolean;
  sortKey: InvoiceSortKey | null;
  sortDirection: 'asc' | 'desc' | null;
  emptyMessage?: string;
  compact?: boolean;
  onToggleSelectAll: () => void;
  onToggleRow: (invoiceId: string) => void;
  onSort: (key: InvoiceSortKey) => void;
  onView: (row: BankSlipItem) => void;
  onDownload: (row: BankSlipItem) => void;
  onSendEmail: (row: BankSlipItem) => void;
  className?: string;
};

const MenuDotsIcon = (): JSX.Element => (
  <svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="3" r="1.25" />
    <circle cx="8" cy="8" r="1.25" />
    <circle cx="8" cy="13" r="1.25" />
  </svg>
);

const InvoiceTable = ({
  rows,
  selectedIds,
  selectAllChecked,
  sortKey,
  sortDirection,
  emptyMessage = 'Nenhuma informação a ser exibida.',
  compact = false,
  onToggleSelectAll,
  onToggleRow,
  onSort,
  onView,
  onDownload,
  onSendEmail,
  className,
}: InvoiceTableProps): JSX.Element => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const selectableRows = useMemo(
    () => rows.filter(({ invoice }) => isInvoiceDownloadable(invoice)),
    [rows]
  );

  useEffect(() => {
    if (!openMenuId) return undefined;

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;

      if (!target?.closest('[data-invoice-menu]')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const getSortDirection = (key: InvoiceSortKey): 'asc' | 'desc' | null =>
    sortKey === key ? sortDirection : null;

  const emptyColSpan = compact ? 3 : 5;

  return (
    <TableSection $compact={compact} className={className}>
      <Table
        className="intro-notas"
        size={compact ? 'sm' : 'md'}
        headerBackgroundColor="#fff"
        headerColor="#575757"
        striped={!compact}
      >
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell align="center" width={compact ? '44px' : '52px'}>
              <CheckboxCell>
                <Checkbox
                  aria-label="Selecionar todas as notas"
                  checked={selectAllChecked && selectableRows.length > 0}
                  disabled={selectableRows.length === 0}
                  onChange={onToggleSelectAll}
                />
              </CheckboxCell>
            </Table.HeaderCell>
            <Table.HeaderCell
              align={compact ? 'left' : 'center'}
              sortable
              sortDirection={getSortDirection('nota')}
              onSort={() => onSort('nota')}
            >
              Nº da nota
            </Table.HeaderCell>
            {!compact && (
              <>
                <Table.HeaderCell
                  align="center"
                  sortable
                  sortDirection={getSortDirection('emissao')}
                  onSort={() => onSort('emissao')}
                >
                  Data de emissão
                </Table.HeaderCell>
                <Table.HeaderCell
                  align="center"
                  sortable
                  sortDirection={getSortDirection('valor')}
                  onSort={() => onSort('valor')}
                >
                  Valor
                </Table.HeaderCell>
              </>
            )}
            {compact ? (
              <Table.HeaderCell
                align="right"
                sortable
                sortDirection={getSortDirection('emissao')}
                onSort={() => onSort('emissao')}
              >
                Referência
              </Table.HeaderCell>
            ) : (
              <Table.HeaderCell align="center" width="56px" />
            )}
          </Table.Row>
          {!compact && <HeaderDividerRow />}
        </Table.Head>

        <Table.Body striped={!compact}>
          {rows.length > 0 ? (
            <>
              {rows.map((row, index) => {
                const { invoice, installment } = row;
                const rowKey =
                  installment.installmentId != null
                    ? `${invoice.invoiceId}-${installment.installmentId}`
                    : `${invoice.invoiceId}-${index}`;
                const canDownload = isInvoiceDownloadable(invoice);
                const isSelected = selectedIds.includes(invoice.invoiceId);

                return (
                  <Table.Row
                    key={rowKey}
                    className={!compact && index % 2 === 0 ? 'row-even' : undefined}
                    data-selected={compact && isSelected ? 'true' : undefined}
                  >
                    <Table.Cell align="center">
                      <CheckboxCell
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <Checkbox
                          aria-label={`Selecionar nota ${invoice.invoiceNumber ?? invoice.invoiceId}`}
                          checked={isSelected}
                          disabled={!canDownload}
                          onChange={() => onToggleRow(invoice.invoiceId)}
                        />
                      </CheckboxCell>
                    </Table.Cell>
                    <Table.Cell align={compact ? 'left' : 'center'}>
                      <InvoiceNumberCell $compact={compact}>
                        <InvoiceNumber $compact={compact}>
                          {invoice.invoiceNumber ?? '-'}
                        </InvoiceNumber>
                        {compact && (
                          <ViewButton
                            type="button"
                            aria-label={`Visualizar nota ${invoice.invoiceNumber ?? invoice.invoiceId}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              onView(row);
                            }}
                          >
                            <EyeIcon aria-hidden="true" />
                          </ViewButton>
                        )}
                      </InvoiceNumberCell>
                    </Table.Cell>
                    {!compact && (
                      <>
                        <Table.Cell align="center">
                          {formatEmissionDate(invoice)}
                        </Table.Cell>
                        <Table.Cell align="center">
                          {formatInvoiceAmount(installment)}
                        </Table.Cell>
                      </>
                    )}
                    {compact ? (
                      <Table.Cell align="right">
                        <ReferenceLabel>
                          {formatEmissionDate(invoice)}
                        </ReferenceLabel>
                      </Table.Cell>
                    ) : (
                      <Table.Cell align="center">
                        <ActionsCell data-invoice-menu>
                          <ActionsMenuWrap>
                            <Menu
                              open={openMenuId === rowKey}
                              onOpenChange={(open) =>
                                setOpenMenuId(open ? rowKey : null)
                              }
                              align="right"
                              trigger={
                                <MenuTrigger
                                  type="button"
                                  aria-label="Ações da nota"
                                >
                                  <MenuDotsIcon />
                                </MenuTrigger>
                              }
                              items={[
                                {
                                  id: 'download',
                                  label: 'Baixar 2ª via',
                                  icon: <DownloadIcon aria-hidden="true" />,
                                  disabled: !canDownload,
                                  onClick: () => onDownload(row),
                                },
                                {
                                  id: 'email',
                                  label: 'Enviar por e-mail',
                                  icon: <MailIcon aria-hidden="true" />,
                                  disabled: !canDownload,
                                  onClick: () => onSendEmail(row),
                                },
                                {
                                  id: 'view',
                                  label: 'Visualizar',
                                  icon: <EyeIcon aria-hidden="true" />,
                                  onClick: () => onView(row),
                                },
                              ]}
                            />
                          </ActionsMenuWrap>
                        </ActionsCell>
                      </Table.Cell>
                    )}
                  </Table.Row>
                );
              })}
              {!compact && <BodyDividerRow colSpan={5} />}
            </>
          ) : (
            <Table.Empty colSpan={emptyColSpan}>
              <EmptyMessage>{emptyMessage}</EmptyMessage>
            </Table.Empty>
          )}
        </Table.Body>
      </Table>
    </TableSection>
  );
};

export default InvoiceTable;
